import { SentryProvider } from "@aptedge/lib-ui/src/context/SentryContext";
import { HOMEPAGE_LANDED_KEY } from "@aptedge/lib-ui/src/hooks/useLocalStorage";
import { useAppDispatch } from "@aptedge/lib-ui/src/redux/hook/hook";
import { updateAnswer } from "@aptedge/lib-ui/src/redux/reduxSlice/answerGPTSlice";
import {
  resetSearch,
  updateAnswerId,
  updateResultId
} from "@aptedge/lib-ui/src/redux/reduxSlice/searchSlice";
import type { AppDispatch } from "@aptedge/lib-ui/src/redux/store/store";
import theme from "@aptedge/lib-ui/src/themeConfigs/muiTheme";
import { QUERY_PARAMS } from "@aptedge/lib-ui/src/types/entities";
import { setAppType } from "@aptedge/lib-ui/src/utils/utils";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { History } from "history";
import { FC, useEffect, useLayoutEffect, useState } from "react";
import {
  BrowserRouter,
  Router,
  useLocation,
  useNavigate
} from "react-router-dom";
import { Toaster } from "sonner";
import { ContainerProvider } from "../../../lib-support-ui/src/context/ContainerContext";
import { toQueryString } from "../clients/utils/url";
import { Routes } from "../routes/routes";
import { AuthProvider } from "./AuthContext";

const syncSearchParamsFromUrl = (
  search: string,
  dispatch: AppDispatch
): void => {
  const queryParams = new URLSearchParams(search);

  /* this line of code sets the answerId redux state if it exists in queryparams while user
      clicks on forward and back button of browser thus maintaining answerId in browser history.
      Also moved the answerId state from answergpt slice to search slice to handle race around condition
      when we change route and update state at the same time on back button click.  */
  dispatch(updateAnswerId(queryParams.get(QUERY_PARAMS.ANSWER_ID) ?? ""));
  dispatch(updateResultId(queryParams.get(QUERY_PARAMS.RESULT_ID) ?? ""));
};

interface Props {
  queryClient: QueryClient;
  history?: History;
  children?: React.ReactNode;
}

const AppProviders: FC<Props> = (props) => {
  const { children, queryClient, history } = props;
  const dispatch = useAppDispatch();
  setAppType((apps) => apps.webApp);

  useEffect(() => {
    const handleBackButtonPressed = (): void => {
      syncSearchParamsFromUrl(window.location.search, dispatch);
    };
    window.addEventListener("popstate", handleBackButtonPressed);
    return () =>
      window.removeEventListener("popstate", handleBackButtonPressed);
  }, [dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <Toaster />
      <QueryClientProvider client={queryClient}>
        <SentryProvider>
          <AuthProvider>
            <ContainerProvider>
              {history ? (
                <HistoryRouter history={history}>
                  <UnauthorizedRedirectHandler>
                    {children}
                  </UnauthorizedRedirectHandler>
                </HistoryRouter>
              ) : (
                <BrowserRouter>
                  <UnauthorizedRedirectHandler>
                    {children}
                  </UnauthorizedRedirectHandler>
                </BrowserRouter>
              )}
            </ContainerProvider>
          </AuthProvider>
        </SentryProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

interface UnauthorizedRedirectHandlerProps {
  children?: React.ReactNode;
}

const AppStateSyncHandler: FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  useEffect(() => {
    const handler = (): void => {
      dispatch(updateAnswer([]));
      dispatch(resetSearch());
      // The next login opens on the homepage again.
      sessionStorage.removeItem(HOMEPAGE_LANDED_KEY);
    };
    window.addEventListener("app:logout", handler);
    return () => window.removeEventListener("app:logout", handler);
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === Routes.GLOBAL_SEARCH && location.search) {
      syncSearchParamsFromUrl(location.search, dispatch);
    }
  }, [location.pathname, location.search, dispatch]);

  return null;
};

const UnauthorizedRedirectHandler: FC<UnauthorizedRedirectHandlerProps> = ({
  children
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event: Event): void => {
      const custom = event as CustomEvent<{ redirectTo?: string }>;
      const redirectTo = custom.detail?.redirectTo;
      const search = toQueryString({ redirectTo });
      navigate({ pathname: Routes.LOGOUT, search }, { replace: true });
    };

    window.addEventListener("app:unauthorized", handler as EventListener);
    return () => {
      window.removeEventListener("app:unauthorized", handler as EventListener);
    };
  }, [navigate]);

  return (
    <>
      <AppStateSyncHandler />
      {children}
    </>
  );
};

interface HistoryRouterProps {
  history: History;
  children?: React.ReactNode;
}

const HistoryRouter: FC<HistoryRouterProps> = ({ history, children }) => {
  const [state, setState] = useState({
    action: history.action,
    location: history.location
  });

  useLayoutEffect(() => {
    const unlisten = history.listen((location, action) => {
      setState({ action, location });
    });
    return unlisten;
  }, [history]);

  return (
    <Router location={state.location} navigator={history}>
      {children}
    </Router>
  );
};

export { AppProviders };
